import React, { useState, useEffect } from 'react';
import { getCandidatesList, addNewCandidate, updateExistingCandidate, getCandidateDetails, deleteCandidateById, getMyCandidate } from '../services/api';
import ResumeParser from '../components/ResumeParser';
import ImportRequirementsModal from '../components/ImportRequirementsModal';
import '../styles/CandidateManagement.css';

// Decode JWT payload for role detection
const decodeToken = () => {
  try {
    const token = localStorage.getItem('admin_token');
    if (!token) return {};
    const payload = token.split('.')[1];
    return JSON.parse(atob(payload.replace(/-/g, '+').replace(/_/g, '/')));
  } catch { return {}; }
};

// Coerce an array item to a renderable string. The resume parser is supposed
// to return string[] for fields like projects_awards / publications_presentations
// but the LLM has been known to "helpfully" return objects with keys like
// {project_name, technologies_used, achievements} (React error #31 blanks the
// whole Achievements tab when that happens). This is the defense: never trust
// upstream shape, always render a string.
const stringifyArrayItem = (item) => {
  if (item == null) return '';
  if (typeof item === 'string') return item;
  if (typeof item === 'number' || typeof item === 'boolean') return String(item);
  if (Array.isArray(item)) {
    return item.map(stringifyArrayItem).filter(Boolean).join(', ');
  }
  if (typeof item === 'object') {
    // Flatten {project_name, technologies_used, achievements} →
    // "Project — Tech A, Tech B — Achievement X" using whatever
    // primitive/array values exist. Object keys we deliberately drop
    // (the labels are obvious from context).
    return Object.values(item)
      .map(stringifyArrayItem)
      .filter(Boolean)
      .join(' — ');
  }
  return String(item);
};

// Helper component for array inputs (skills, responsibilities, etc.)
const ArrayInputField = ({ label, values, onChange, placeholder }) => {
  const [newItem, setNewItem] = useState('');
  // Normalize once on every render — cheap, and keeps the rest of the
  // component dealing in plain strings. We don't push the normalized
  // shape back via onChange to avoid a render loop; the form state
  // load path (handleEditCandidate, handleResumeParse) is the right
  // place to persist the flat shape.
  const safeValues = Array.isArray(values) ? values.map(stringifyArrayItem) : [];

  const handleAdd = () => {
    if (newItem.trim()) {
      onChange([...safeValues, newItem.trim()]);
      setNewItem('');
    }
  };

  const handleRemove = (index) => {
    const updatedValues = [...safeValues];
    updatedValues.splice(index, 1);
    onChange(updatedValues);
  };

  return (
    <div className="array-input-field">
      <label>{label}</label>
      <div className="array-input-controls">
        <input
          type="text"
          value={newItem}
          onChange={(e) => setNewItem(e.target.value)}
          placeholder={placeholder}
        />
        <button
          type="button"
          onClick={handleAdd}
          className="add-button"
        >
          Add
        </button>
      </div>
      <ul className="array-list">
        {safeValues.map((item, index) => (
          <li key={index}>
            <span>{item}</span>
            <button
              type="button"
              onClick={() => handleRemove(index)}
              className="remove-button"
            >
              Remove
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CandidateManagement = () => {
  const currentUser = decodeToken();
  const isInterviewPrep = currentUser.role === 'interview_prep';

  const [candidates, setCandidates] = useState([]);
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [activePage, setActivePage] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredCandidates, setFilteredCandidates] = useState([]);
  const [error, setError] = useState('');
  const [showParser, setShowParser] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [autoFilledFields, setAutoFilledFields] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [form, setForm] = useState({
    personal_information: {
      first_name: '',
      last_name: '',
      date_of_birth: '',
      email: '',
      phone_number: '',
      address: '',
      current_location: '',
    },
    education_qualifications: {
      highest_education_level: '',
      university_name: '',
      degree_name: '',
      specialization: '',
      percentage_marks: 0,
      certificates_diplomas: [],
    },
    work_experience: [
      {
        company_name: '',
        job_title: '',
        start_date: '',
        end_date: '',
        key_responsibilities: [],
        achievements: [],
      },
    ],
    technical_skills: {
      programming_languages: [],
      development_tools: [],
      operating_systems: [],
      database_management_systems: [],
      cloud_platforms: [],
    },
    soft_skills: {
      communication: '',
      teamwork: '',
      problem_solving: '',
      time_management: '',
      adaptability: '',
    },
    achievements_awards: {
      projects_awards: [],
      publications_presentations: [],
    },
    availability_preferences: {
      expected_salary: 0,
      current_salary: 0,
      job_location: '',
      job_type: '',
      work_environment: '',
      notice_period: '',
      immediate_joiner: false,
    },
    certifications_licenses: {
      certificates_licenses: [],
    },
    additional_information: {
      hobbies_interests: '',
      references: [],
      reason_for_role_interest: '',
      reason_for_leaving: '',
      cv_link: '',
    },
    requirement_information: {
      requirement_id: '',
    },
  });

  useEffect(() => {
    fetchCandidates();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      setFilteredCandidates(
        candidates.filter((cand) =>
          `${cand.personal_information?.first_name || ''} ${cand.personal_information?.last_name || ''}`
            .toLowerCase()
            .includes(searchTerm.toLowerCase()) ||
          (cand.personal_information?.email || '').toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    } else {
      setFilteredCandidates(candidates);
    }
  }, [searchTerm, candidates]);

  const fetchCandidates = async () => {
    try {
      setError('');
      if (isInterviewPrep) {
        // Candidate self-service: fetch only own profile
        const result = await getMyCandidate();
        if (result.found && result.candidate) {
          setCandidates([result.candidate]);
          setFilteredCandidates([result.candidate]);
        } else {
          setCandidates([]);
          setFilteredCandidates([]);
        }
      } else {
        const data = await getCandidatesList();
        setCandidates(data);
        setFilteredCandidates(data);
      }
    } catch (err) {
      console.error('Error fetching candidates:', err);
      setError('Failed to load candidates.');
      setCandidates([]);
      setFilteredCandidates([]);
    }
  };

  const handleParsedData = (parsedData, fieldsAutoFilled) => {
    // The resume parser is *supposed* to return flat string arrays for
    // these two fields, but the LLM has been observed returning objects
    // like {project_name, technologies_used, achievements}. Coerce to
    // strings here so the form state matches the backend schema and the
    // Achievements tab cannot crash. (Belt & suspenders alongside the
    // ArrayInputField render-time normalization.)
    const safeParsed = { ...parsedData };
    if (safeParsed.achievements_awards) {
      safeParsed.achievements_awards = {
        ...safeParsed.achievements_awards,
        projects_awards: (safeParsed.achievements_awards.projects_awards || []).map(stringifyArrayItem),
        publications_presentations: (safeParsed.achievements_awards.publications_presentations || []).map(stringifyArrayItem),
      };
    }

    // Merge parsed data into form
    setForm(prevForm => ({
      ...prevForm,
      ...safeParsed,
    }));
    setAutoFilledFields(fieldsAutoFilled);
    setShowParser(false);
  };

  const handleInputChange = (section, field, value) => {
    setForm({
      ...form,
      [section]: {
        ...form[section],
        [field]: value,
      },
    });
  };

  const updateWorkExperience = (experiences) => {
    setForm({
      ...form,
      work_experience: experiences,
    });
  };

  const handleEdit = async (id) => {
    try {
      setError('');
      const cand = await getCandidateDetails(id);
      
      // Ensure all date fields are properly formatted and handle null values
      const sanitizedCandidate = {
        ...cand,
        personal_information: {
          first_name: cand.personal_information?.first_name || '',
          last_name: cand.personal_information?.last_name || '',
          date_of_birth: cand.personal_information?.date_of_birth 
            ? (typeof cand.personal_information.date_of_birth === 'string' 
              ? cand.personal_information.date_of_birth.substring(0, 10) 
              : new Date(cand.personal_information.date_of_birth).toISOString().substring(0, 10))
            : '',
          email: cand.personal_information?.email || '',
          phone_number: cand.personal_information?.phone_number || '',
          address: cand.personal_information?.address || '',
        },
        education_qualifications: {
          highest_education_level: cand.education_qualifications?.highest_education_level || '',
          university_name: cand.education_qualifications?.university_name || '',
          degree_name: cand.education_qualifications?.degree_name || '',
          specialization: cand.education_qualifications?.specialization || '',
          percentage_marks: cand.education_qualifications?.percentage_marks || 0,
          certificates_diplomas: cand.education_qualifications?.certificates_diplomas || [],
        },
        work_experience: (cand.work_experience || []).map(exp => ({
          company_name: exp.company_name || '',
          job_title: exp.job_title || '',
          start_date: exp.start_date 
            ? (typeof exp.start_date === 'string' 
              ? exp.start_date.substring(0, 10) 
              : new Date(exp.start_date).toISOString().substring(0, 10))
            : '',
          end_date: exp.end_date 
            ? (typeof exp.end_date === 'string' 
              ? exp.end_date.substring(0, 10) 
              : new Date(exp.end_date).toISOString().substring(0, 10))
            : '',
          key_responsibilities: exp.key_responsibilities || [],
          achievements: exp.achievements || [],
        })),
        technical_skills: {
          programming_languages: cand.technical_skills?.programming_languages || [],
          development_tools: cand.technical_skills?.development_tools || [],
          operating_systems: cand.technical_skills?.operating_systems || [],
          database_management_systems: cand.technical_skills?.database_management_systems || [],
          cloud_platforms: cand.technical_skills?.cloud_platforms || [],
        },
        soft_skills: {
          communication: cand.soft_skills?.communication || '',
          teamwork: cand.soft_skills?.teamwork || '',
          problem_solving: cand.soft_skills?.problem_solving || '',
          time_management: cand.soft_skills?.time_management || '',
          adaptability: cand.soft_skills?.adaptability || '',
        },
        achievements_awards: {
          // Old records (and the LLM resume parser) may have stored these as
          // arrays of objects. Flatten on load so the form state is always
          // string[] — matches what ArrayInputField expects and what the
          // backend schema actually says these are.
          projects_awards: (cand.achievements_awards?.projects_awards || []).map(stringifyArrayItem),
          publications_presentations: (cand.achievements_awards?.publications_presentations || []).map(stringifyArrayItem),
        },
        availability_preferences: {
          expected_salary: cand.availability_preferences?.expected_salary || 0,
          job_location: cand.availability_preferences?.job_location || '',
          job_type: cand.availability_preferences?.job_type || '',
          work_environment: cand.availability_preferences?.work_environment || '',
        },
        certifications_licenses: {
          certificates_licenses: cand.certifications_licenses?.certificates_licenses || [],
        },
        additional_information: {
          hobbies_interests: cand.additional_information?.hobbies_interests || '',
          references: cand.additional_information?.references || [],
          reason_for_role_interest: cand.additional_information?.reason_for_role_interest || '',
          cv_link: cand.additional_information?.cv_link || '',
        },
        requirement_information: {
          requirement_id: cand.requirement_information?.requirement_id || '',
        },
      };
      
      setForm(sanitizedCandidate);
      setSelectedCandidate(cand);
      setIsEditing(true);
      setIsModalOpen(true);
      setActivePage(0);
    } catch (err) {
      console.error('Error fetching candidate details:', err);
      setError('Failed to fetch candidate details.');
    }
  };

  const handleDelete = async (id, name) => {
    if (!window.confirm(`Are you sure you want to delete candidate "${name}"? This action cannot be undone.`)) return;
    try {
      await deleteCandidateById(id);
      setCandidates(candidates.filter((c) => c._id !== id));
    } catch (err) {
      console.error('Error deleting candidate:', err);
      setError('Failed to delete candidate.');
    }
  };

  const handleAddNew = () => {
    resetForm();
    setIsEditing(false);
    setIsModalOpen(true);
    setActivePage(0);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;          // prevent duplicate submissions
    setIsSubmitting(true);
    setError('');
    
    try {
      if (isEditing && selectedCandidate) {
        await updateExistingCandidate(selectedCandidate._id, form);
      } else {
        await addNewCandidate(form);
      }
      fetchCandidates();
      closeModal();
    } catch (err) {
      console.error('Error saving candidate:', err);
      setError('Failed to save candidate. Please check all required fields.');
    } finally {
      setIsSubmitting(false);          // always re-enable, even on error
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  const resetForm = () => {
    setForm({
      personal_information: {
        first_name: '',
        last_name: '',
        date_of_birth: '',
        email: '',
        phone_number: '',
        address: '',
      },
      education_qualifications: {
        highest_education_level: '',
        university_name: '',
        degree_name: '',
        specialization: '',
        percentage_marks: 0,
        certificates_diplomas: [],
      },
      work_experience: [
        {
          company_name: '',
          job_title: '',
          start_date: '',
          end_date: '',
          key_responsibilities: [],
          achievements: [],
        },
      ],
      technical_skills: {
        programming_languages: [],
        development_tools: [],
        operating_systems: [],
        database_management_systems: [],
        cloud_platforms: [],
      },
      soft_skills: {
        communication: '',
        teamwork: '',
        problem_solving: '',
        time_management: '',
        adaptability: '',
      },
      achievements_awards: {
        projects_awards: [],
        publications_presentations: [],
      },
      availability_preferences: {
        expected_salary: 0,
        job_location: '',
        job_type: '',
        work_environment: '',
      },
      certifications_licenses: {
        certificates_licenses: [],
      },
      additional_information: {
        hobbies_interests: '',
        references: [],
        reason_for_role_interest: '',
        cv_link: '',
      },
      requirement_information: {
        requirement_id: '',
      },
    });
    setSelectedCandidate(null);
    setIsEditing(false);
    setError('');
  };

  // Work experience component
  const renderWorkExperience = () => {
    const addExperience = () => {
      const updated = [...form.work_experience, {
        company_name: '',
        job_title: '',
        start_date: '',
        end_date: '',
        key_responsibilities: [],
        achievements: [],
      }];
      updateWorkExperience(updated);
    };

    const removeExperience = (index) => {
      const updated = [...form.work_experience];
      updated.splice(index, 1);
      updateWorkExperience(updated);
    };

    const updateExperience = (index, field, value) => {
      const updated = [...form.work_experience];
      updated[index][field] = value;
      updateWorkExperience(updated);
    };

    return (
      <div className="work-experience-section">
        <div className="section-header">
          <h3>Work Experience</h3>
          <button
            type="button"
            onClick={addExperience}
            className="add-experience-button"
          >
            Add Experience
          </button>
        </div>

        {form.work_experience.map((exp, index) => (
          <div key={index} className="experience-item">
            <div className="experience-header">
              <h4>Experience #{index + 1}</h4>
              <button
                type="button"
                onClick={() => removeExperience(index)}
                className="remove-experience-button"
              >
                Remove
              </button>
            </div>
            
            <div className="experience-grid">
              <div className="form-group">
                <label>Company Name</label>
                <input
                  type="text"
                  value={exp.company_name || ''}
                  onChange={(e) => updateExperience(index, 'company_name', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Job Title</label>
                <input
                  type="text"
                  value={exp.job_title || ''}
                  onChange={(e) => updateExperience(index, 'job_title', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={exp.start_date || ''}
                  onChange={(e) => updateExperience(index, 'start_date', e.target.value)}
                  required
                />
              </div>
              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={exp.end_date || ''}
                  onChange={(e) => updateExperience(index, 'end_date', e.target.value)}
                />
              </div>
            </div>

            <ArrayInputField
              label="Key Responsibilities"
              values={exp.key_responsibilities || []}
              onChange={(value) => updateExperience(index, 'key_responsibilities', value)}
              placeholder="Add responsibility"
            />

            <ArrayInputField
              label="Achievements"
              values={exp.achievements || []}
              onChange={(value) => updateExperience(index, 'achievements', value)}
              placeholder="Add achievement"
            />
          </div>
        ))}
      </div>
    );
  };

  // Render personal information form
  const renderPersonalInfoForm = () => (
    <div className="personal-info-section">
      <div className="form-row">
        <div className="form-group">
          <label>First Name*</label>
          <input
            type="text"
            value={form.personal_information?.first_name || ''}
            onChange={(e) => handleInputChange('personal_information', 'first_name', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Last Name*</label>
          <input
            type="text"
            value={form.personal_information?.last_name || ''}
            onChange={(e) => handleInputChange('personal_information', 'last_name', e.target.value)}
            required
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Date of Birth</label>
          <input
            type="date"
            value={form.personal_information?.date_of_birth || ''}
            onChange={(e) => handleInputChange('personal_information', 'date_of_birth', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email*</label>
          <input
            type="email"
            value={form.personal_information?.email || ''}
            onChange={(e) => handleInputChange('personal_information', 'email', e.target.value)}
            required
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Phone Number*</label>
          <input
            type="tel"
            value={form.personal_information?.phone_number || ''}
            onChange={(e) => handleInputChange('personal_information', 'phone_number', e.target.value)}
            required
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group full-width">
          <label>Address</label>
          <textarea
            value={form.personal_information?.address || ''}
            onChange={(e) => handleInputChange('personal_information', 'address', e.target.value)}
            rows="3"
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Current Location</label>
          <input
            type="text"
            value={form.personal_information?.current_location || ''}
            onChange={(e) => handleInputChange('personal_information', 'current_location', e.target.value)}
            placeholder="City, State/Country"
            className={autoFilledFields.includes('personal_information.current_location') ? 'auto-filled' : ''}
          />
        </div>
      </div>
    </div>
  );

  // Render education form
  const renderEducationForm = () => (
    <div className="education-section">
      <div className="form-row">
        <div className="form-group">
          <label>Highest Education Level*</label>
          <select
            value={form.education_qualifications?.highest_education_level || ''}
            onChange={(e) => handleInputChange('education_qualifications', 'highest_education_level', e.target.value)}
            required
          >
            <option value="">Select Education Level</option>
            <option value="High School">High School</option>
            <option value="Associate's Degree">Associate's Degree</option>
            <option value="Bachelor's Degree">Bachelor's Degree</option>
            <option value="Master's Degree">Master's Degree</option>
            <option value="Doctorate">Doctorate</option>
            <option value="Other">Other</option>
          </select>
        </div>
        <div className="form-group">
          <label>University/Institution Name*</label>
          <input
            type="text"
            value={form.education_qualifications?.university_name || ''}
            onChange={(e) => handleInputChange('education_qualifications', 'university_name', e.target.value)}
            required
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Degree Name*</label>
          <input
            type="text"
            value={form.education_qualifications?.degree_name || ''}
            onChange={(e) => handleInputChange('education_qualifications', 'degree_name', e.target.value)}
            required
          />
        </div>
        <div className="form-group">
          <label>Specialization</label>
          <input
            type="text"
            value={form.education_qualifications?.specialization || ''}
            onChange={(e) => handleInputChange('education_qualifications', 'specialization', e.target.value)}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Percentage/CGPA</label>
          <input
            type="number"
            step="0.01"
            min="0"
            max="100"
            value={form.education_qualifications?.percentage_marks || ''}
            onChange={(e) => handleInputChange('education_qualifications', 'percentage_marks', parseFloat(e.target.value) || 0)}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group full-width">
          <ArrayInputField
            label="Certificates/Diplomas"
            values={form.education_qualifications?.certificates_diplomas || []}
            onChange={(value) => handleInputChange('education_qualifications', 'certificates_diplomas', value)}
            placeholder="Add certificate or diploma"
          />
        </div>
      </div>
    </div>
  );

  // Render skills form
  const renderSkillsForm = () => (
    <div className="skills-section">
      <fieldset>
        <legend>Technical Skills</legend>
        <div className="form-row">
          <div className="form-group">
            <ArrayInputField
              label="Programming Languages"
              values={form.technical_skills?.programming_languages || []}
              onChange={(value) => handleInputChange('technical_skills', 'programming_languages', value)}
              placeholder="Add programming language"
            />
          </div>
          <div className="form-group">
            <ArrayInputField
              label="Development Tools"
              values={form.technical_skills?.development_tools || []}
              onChange={(value) => handleInputChange('technical_skills', 'development_tools', value)}
              placeholder="Add development tool"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <ArrayInputField
              label="Operating Systems"
              values={form.technical_skills?.operating_systems || []}
              onChange={(value) => handleInputChange('technical_skills', 'operating_systems', value)}
              placeholder="Add operating system"
            />
          </div>
          <div className="form-group">
            <ArrayInputField
              label="Database Management Systems"
              values={form.technical_skills?.database_management_systems || []}
              onChange={(value) => handleInputChange('technical_skills', 'database_management_systems', value)}
              placeholder="Add database system"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <ArrayInputField
              label="Cloud Platforms"
              values={form.technical_skills?.cloud_platforms || []}
              onChange={(value) => handleInputChange('technical_skills', 'cloud_platforms', value)}
              placeholder="Add cloud platform"
            />
          </div>
        </div>
      </fieldset>
      
      <fieldset>
        <legend>Soft Skills</legend>
        <div className="form-row">
          <div className="form-group">
            <label>Communication</label>
            <select
              value={form.soft_skills?.communication || ''}
              onChange={(e) => handleInputChange('soft_skills', 'communication', e.target.value)}
            >
              <option value="">Select Level</option>
              <option value="Basic">Basic</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
          <div className="form-group">
            <label>Teamwork</label>
            <select
              value={form.soft_skills?.teamwork || ''}
              onChange={(e) => handleInputChange('soft_skills', 'teamwork', e.target.value)}
            >
              <option value="">Select Level</option>
              <option value="Basic">Basic</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Problem Solving</label>
            <select
              value={form.soft_skills?.problem_solving || ''}
              onChange={(e) => handleInputChange('soft_skills', 'problem_solving', e.target.value)}
            >
              <option value="">Select Level</option>
              <option value="Basic">Basic</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
          <div className="form-group">
            <label>Time Management</label>
            <select
              value={form.soft_skills?.time_management || ''}
              onChange={(e) => handleInputChange('soft_skills', 'time_management', e.target.value)}
            >
              <option value="">Select Level</option>
              <option value="Basic">Basic</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>Adaptability</label>
            <select
              value={form.soft_skills?.adaptability || ''}
              onChange={(e) => handleInputChange('soft_skills', 'adaptability', e.target.value)}
            >
              <option value="">Select Level</option>
              <option value="Basic">Basic</option>
              <option value="Intermediate">Intermediate</option>
              <option value="Advanced">Advanced</option>
              <option value="Expert">Expert</option>
            </select>
          </div>
        </div>
      </fieldset>
    </div>
  );

  // Render achievements and additional info form
  const renderAdditionalInfoForm = () => (
    <div className="additional-info-section">
      <fieldset>
        <legend>Achievements & Awards</legend>
        <div className="form-row">
          <div className="form-group">
            <ArrayInputField
              label="Projects & Awards"
              values={form.achievements_awards?.projects_awards || []}
              onChange={(value) => handleInputChange('achievements_awards', 'projects_awards', value)}
              placeholder="Add project or award"
            />
          </div>
          <div className="form-group">
            <ArrayInputField
              label="Publications & Presentations"
              values={form.achievements_awards?.publications_presentations || []}
              onChange={(value) => handleInputChange('achievements_awards', 'publications_presentations', value)}
              placeholder="Add publication or presentation"
            />
          </div>
        </div>
      </fieldset>
      
      <fieldset>
        <legend>Certifications & Licenses</legend>
        <div className="form-row">
          <div className="form-group full-width">
            <ArrayInputField
              label="Certificates & Licenses"
              values={form.certifications_licenses?.certificates_licenses || []}
              onChange={(value) => handleInputChange('certifications_licenses', 'certificates_licenses', value)}
              placeholder="Add certification or license"
            />
          </div>
        </div>
      </fieldset>
      
      <fieldset>
        <legend>Additional Information</legend>
        <div className="form-row">
          <div className="form-group full-width">
            <label>Hobbies & Interests</label>
            <textarea
              value={form.additional_information?.hobbies_interests || ''}
              onChange={(e) => handleInputChange('additional_information', 'hobbies_interests', e.target.value)}
              rows="3"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group full-width">
            <ArrayInputField
              label="References"
              values={form.additional_information?.references || []}
              onChange={(value) => handleInputChange('additional_information', 'references', value)}
              placeholder="Add reference"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group full-width">
            <label>Reason for Interest in Role</label>
            <textarea
              value={form.additional_information?.reason_for_role_interest || ''}
              onChange={(e) => handleInputChange('additional_information', 'reason_for_role_interest', e.target.value)}
              rows="3"
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group full-width">
            <label>Reason for Leaving Current Company</label>
            <textarea
              value={form.additional_information?.reason_for_leaving || ''}
              onChange={(e) => handleInputChange('additional_information', 'reason_for_leaving', e.target.value)}
              rows="3"
              className={autoFilledFields.includes('additional_information.reason_for_leaving') ? 'auto-filled' : ''}
            />
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label>CV/Resume Link</label>
            <input
              type="text"
              value={form.additional_information?.cv_link || ''}
              onChange={(e) => handleInputChange('additional_information', 'cv_link', e.target.value)}
              placeholder="https://..."
            />
          </div>
        </div>
      </fieldset>
    </div>
  );

  // Render availability and preferences form
  const renderAvailabilityForm = () => (
    <div className="availability-section">
      <div className="form-row">
        <div className="form-group">
          <label>Expected Salary</label>
          <input
            type="number"
            min="0"
            value={form.availability_preferences?.expected_salary || ''}
            onChange={(e) => handleInputChange('availability_preferences', 'expected_salary', parseFloat(e.target.value) || 0)}
          />
        </div>
        <div className="form-group">
          <label>Job Location Preference</label>
          <input
            type="text"
            value={form.availability_preferences?.job_location || ''}
            onChange={(e) => handleInputChange('availability_preferences', 'job_location', e.target.value)}
          />
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Job Type</label>
          <select
            value={form.availability_preferences?.job_type || ''}
            onChange={(e) => handleInputChange('availability_preferences', 'job_type', e.target.value)}
          >
            <option value="">Select Job Type</option>
            <option value="Full-time">Full-time</option>
            <option value="Part-time">Part-time</option>
            <option value="Contract">Contract</option>
            <option value="Freelance">Freelance</option>
            <option value="Internship">Internship</option>
          </select>
        </div>
        <div className="form-group">
          <label>Work Environment</label>
          <select
            value={form.availability_preferences?.work_environment || ''}
            onChange={(e) => handleInputChange('availability_preferences', 'work_environment', e.target.value)}
          >
            <option value="">Select Environment</option>
            <option value="Onsite">Onsite</option>
            <option value="Remote">Remote</option>
            <option value="Hybrid">Hybrid</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Current Salary (Annual)</label>
          <input
            type="number"
            min="0"
            value={form.availability_preferences?.current_salary || ''}
            onChange={(e) => handleInputChange('availability_preferences', 'current_salary', parseFloat(e.target.value) || 0)}
            className={autoFilledFields.includes('availability_preferences.current_salary') ? 'auto-filled' : ''}
          />
        </div>
        <div className="form-group">
          <label>Notice Period</label>
          <select
            value={form.availability_preferences?.notice_period || ''}
            onChange={(e) => handleInputChange('availability_preferences', 'notice_period', e.target.value)}
            className={autoFilledFields.includes('availability_preferences.notice_period') ? 'auto-filled' : ''}
          >
            <option value="">Select Notice Period</option>
            <option value="immediate">Immediate</option>
            <option value="15 days">15 Days</option>
            <option value="30 days">30 Days</option>
            <option value="60 days">60 Days</option>
            <option value="90 days">90 Days</option>
          </select>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>
            <input
              type="checkbox"
              checked={form.availability_preferences?.immediate_joiner || false}
              onChange={(e) => handleInputChange('availability_preferences', 'immediate_joiner', e.target.checked)}
              className={autoFilledFields.includes('availability_preferences.immediate_joiner') ? 'auto-filled' : ''}
            />
            {' '}Immediate Joiner
          </label>
        </div>
      </div>
      <div className="form-row">
        <div className="form-group">
          <label>Requirement ID</label>
          <input
            type="text"
            value={form.requirement_information?.requirement_id || ''}
            onChange={(e) => handleInputChange('requirement_information', 'requirement_id', e.target.value)}
          />
        </div>
      </div>
    </div>
  );

  // Mapping for form sections
  const formSections = [
    { title: "Personal Information", render: renderPersonalInfoForm },
    { title: "Education", render: renderEducationForm },
    { title: "Work Experience", render: renderWorkExperience },
    { title: "Skills", render: renderSkillsForm },
    { title: "Achievements & Additional Info", render: renderAdditionalInfoForm },
    { title: "Availability & Preferences", render: renderAvailabilityForm }
  ];

  return (
    <div className="candidate-list">
      <div className="header-section">
        <h2>{isInterviewPrep ? 'My Profile' : 'Candidates'}</h2>
        {!isInterviewPrep && (
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="add-button"
              style={{ background: '#27272a', border: '1px solid #3f3f46' }}
              onClick={() => setShowImport(true)}
            >
              Import from source
            </button>
            <button
              className="add-button"
              onClick={handleAddNew}
            >
              Add New Candidate
            </button>
          </div>
        )}
      </div>

      {showImport && (
        <ImportRequirementsModal
          entity="candidates"
          onClose={() => setShowImport(false)}
          onImported={fetchCandidates}
        />
      )}

      {error && <p className="error-message">{error}</p>}

      {!isInterviewPrep && (
        <input
          type="text"
          placeholder="Search by name or email"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-box"
        />
      )}

      <table>
        <thead>
          <tr>
            <th>Name</th>
            <th>Email</th>
            <th>Phone</th>
            <th>Education</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {filteredCandidates.length > 0 ? (
            filteredCandidates.map((cand) => (
              <tr key={cand._id}>
                <td>{`${cand.personal_information?.first_name || ''} ${cand.personal_information?.last_name || ''}`}</td>
                <td>{cand.personal_information?.email || 'N/A'}</td>
                <td>{cand.personal_information?.phone_number || 'N/A'}</td>
                <td>{cand.education_qualifications?.highest_education_level || 'Not specified'}</td>
                <td style={{ display: 'flex', gap: '6px' }}>
                  <button onClick={() => handleEdit(cand._id || cand.candidate_id)}>Edit</button>
                  {!isInterviewPrep && (
                    <button
                      onClick={() => handleDelete(cand._id, `${cand.personal_information?.first_name || ''} ${cand.personal_information?.last_name || ''}`.trim())}
                      style={{ background: '#ef4444', color: 'white', border: 'none', padding: '4px 10px', borderRadius: '4px', cursor: 'pointer' }}
                    >
                      Delete
                    </button>
                  )}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="5" className="no-data">
                {searchTerm ? 'No candidates match your search.' : 'No candidates found.'}
              </td>
            </tr>
          )}
        </tbody>
      </table>

      {/* Modal Dialog */}
      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{isEditing ? 'Edit Candidate' : 'Add New Candidate'}</h3>
              <button className="close-button" onClick={closeModal}>×</button>
            </div>

            {!isEditing && !showParser && (
              <button
                type="button"
                className="parse-resume-btn"
                onClick={() => setShowParser(true)}
                style={{ margin: '16px', padding: '10px 20px', background: '#4caf50', color: 'white', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
              >
                📄 Parse from Resume
              </button>
            )}

            {showParser && (
              <ResumeParser
                onParsed={handleParsedData}
                onClose={() => setShowParser(false)}
              />
            )}

            {!showParser && (
            <div className="modal-body">
              <div className="form-section-tabs">
                {formSections.map((section, index) => (
                  <button
                    key={index}
                    className={`section-tab ${activePage === index ? 'active' : ''}`}
                    onClick={() => setActivePage(index)}
                  >
                    {section.title}
                  </button>
                ))}
              </div>
              
              <form
                onSubmit={handleSave}
                className="candidate-form"
                onKeyDown={(e) => {
                  // Prevent Enter key from auto-submitting the form while the user
                  // is typing inside an <input> field (e.g. pressing Enter to
                  // "confirm" a value).  We intentionally allow Enter on:
                  //   • <textarea>  – should insert a new line
                  //   • <button>    – explicit keyboard activation of the save button
                  //   • <select>    – closes the dropdown, not a submission
                  if (e.key === 'Enter' && e.target.tagName === 'INPUT') {
                    e.preventDefault();
                  }
                }}
              >
                <div className="form-section">
                  {formSections[activePage].render()}
                </div>
                
                <div className="form-navigation">
                  <button
                    type="button"
                    className="prev-button"
                    disabled={activePage === 0}
                    onClick={() => setActivePage(activePage - 1)}
                  >
                    Previous
                  </button>
                  
                  {activePage === formSections.length - 1 ? (
                    <button type="submit" className="save-button" disabled={isSubmitting}>
                      {isSubmitting ? 'Saving...' : (isEditing ? 'Update Candidate' : 'Add Candidate')}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="next-button"
                      onClick={() => setActivePage(activePage + 1)}
                    >
                      Next
                    </button>
                  )}
                </div>
              </form>
            </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CandidateManagement;